import { defineStore, storeToRefs } from "pinia";
import Vue from "vue";
import ME_QUERY from "@/graphql/me.graphql";
import SET_PERSON_GO_TO_HOUSE from "@/graphql/profile/setPersonGoToHouse.graphql";
import CHANGE_PASSWORD from "@/graphql/profile/changePassword.graphql";
import SET_PERSON_HIDE_MOBILE from "@/graphql/profile/setPersonHideMobile.graphql";
import CONNECT_OAUTH_PROVIDER from "@/graphql/connectOauthProvider.graphql";
import DISCONNECT_OAUTH_PROVIDER from "@/graphql/disconnectOauthProvider.graphql";
import SET_PERSON_TYPE from "~/stores/user/setPersonType.graphql";

import { useAdminUserSwitcher } from "~/components/_refactored/admin";
import MY_NOTIFICATIONS_QUERY from "~/graphql/queries/myNotifications.graphql";
import { useAccountSettingsStore } from "~/stores/user/accountSettings";

export const PO_OVERRIDE_COOKIE_NAME = "po-override";
export const PO_OVERRIDE_COOKIE_MAX_AGE = 8 * 60 * 60;

function isQuotaExceededError(error) {
  if (!error) return false;
  // Different browsers signal quota errors differently.
  return (
    error.name === "QuotaExceededError" ||
    error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    error.code === 22 ||
    error.code === 1014
  );
}

export const useUserStore = defineStore("userStore", {
  state: () => ({
    me: null,
    meQuery: null,
    meLoading: false,
    meLoaded: false,
    isSettingsSame: true,
    loading: false,
    notifications: [],
  }),
  getters: {
    isAdmin: (state) => state.me?.isAdmin,
  },
  actions: {
    updateLastPerson() {
      this.$nuxt.$logger.debug("updateLastPerson");
      if (this.me?.isAdmin) {
        this.$nuxt.$logger.debug("is admin, skipping updateLastPerson");
        return;
      }
      const { switchedToMate } = storeToRefs(useAdminUserSwitcher());
      if (switchedToMate.value) {
        this.$nuxt.$logger.debug("switchedToMate skipping updateLastPerson");
        return;
      }

      let lastPerson;
      try {
        lastPerson = JSON.parse(localStorage.getItem("_lp") || null);
      } catch (error) {}

      let nextLastPersons;
      if (
        Array.isArray(lastPerson) &&
        !lastPerson.includes(this.me.lastPerson)
      ) {
        nextLastPersons = [...lastPerson, this.me.lastPerson];
      } else if (!Array.isArray(lastPerson)) {
        if (!lastPerson || lastPerson === this.me.lastPerson) {
          nextLastPersons = [this.me.lastPerson];
        } else if (lastPerson !== this.me.lastPerson) {
          nextLastPersons = [lastPerson, this.me.lastPerson];
        }
      }

      if (!nextLastPersons) return;

      // On quota errors, drop the oldest entries and retry so we keep the
      // current person at the cost of losing older history.
      while (nextLastPersons.length > 0) {
        try {
          localStorage.setItem("_lp", JSON.stringify(nextLastPersons));
          return;
        } catch (error) {
          if (!isQuotaExceededError(error) || nextLastPersons.length === 1) {
            this.$nuxt.$logger.warn(
              "updateLastPerson: localStorage write failed",
              error
            );
            return;
          }
          nextLastPersons.shift();
        }
      }
    },
    async queryMe(skipErrorPageOnAuthError) {
      try {
        if (this.meQuery) {
          // repeat calls should wait for finish
          await this.meQuery;
          return;
        }
        this.meLoading = true;
        this.meQuery = this.$nuxt.$query(
          ME_QUERY,
          {},
          { skipErrorPageOnAuthError }
        );
        const result = await this.meQuery;
        this.me = result.me;
        this.applyPriceOfferOverride();
        this.meLoaded = true;
        return Promise.resolve(true);
      } catch (error) {
        console.error("refetch error", error);
        return Promise.reject(error);
      } finally {
        this.meLoading = false;
        this.meQuery = null;
      }
    },
    setUserValues(payload) {
      if (!this.me) {
        this.me = payload;
        this.applyPriceOfferOverride();
        return;
      }
      for (const key of Object.keys(payload)) {
        if (key.startsWith("__")) {
          // Ignore graphql types
          continue;
        }
        Vue.set(this.me, key, payload[key]);
      }
      this.applyPriceOfferOverride();
    },
    hasPriceOfferOverride() {
      if (!this.me) return false;
      const cookieValue = this.$nuxt.$cookie.get(PO_OVERRIDE_COOKIE_NAME);
      return !!cookieValue && String(cookieValue) === String(this.me.id);
    },
    applyPriceOfferOverride() {
      if (!this.hasPriceOfferOverride()) return;
      Vue.set(this.me, "isPriceOfferProfessional", true);
      Vue.set(this.me, "isMate", false);
      Vue.set(this.me, "isCustomer", false);
    },
    /**
     * Saves whether the person has an unavailability period. Nothing on `me`
     * reflects it.
     * @param {boolean} value
     */
    async setHasUnavailability(value) {
      const SET_HAS_UNAVAILABILITY = await import(
        "./setHasUnavailability.graphql"
      );
      await this.$nuxt.$mutate(SET_HAS_UNAVAILABILITY, { input: value });
    },

    setIsSettingsSame(isSame) {
      this.isSettingsSame = isSame;
    },

    async connectOauthProvider({ provider, token }) {
      await this.$nuxt.$mutate(CONNECT_OAUTH_PROVIDER, { provider, token });
      const accountSettings = useAccountSettingsStore();
      const current =
        accountSettings.connectedAccounts?.connectedOauthProviders ?? [];
      if (!current.some((p) => p.provider === provider)) {
        accountSettings.setValues("connectedAccounts", {
          connectedOauthProviders: [
            ...current,
            { provider, created: new Date().toISOString(), lastUsed: null },
          ],
        });
      }
    },

    async disconnectOauthProvider({ provider }) {
      await this.$nuxt.$mutate(DISCONNECT_OAUTH_PROVIDER, { provider });
      const accountSettings = useAccountSettingsStore();
      accountSettings.setValues("connectedAccounts", {
        connectedOauthProviders: (
          accountSettings.connectedAccounts?.connectedOauthProviders ?? []
        ).filter((p) => p.provider !== provider),
      });
    },

    async setProfilePicture(fileId) {
      try {
        const SET_PROFILE_PICTURE = await import(
          "@/graphql/mutations/setProfilePicture.graphql"
        );
        const resp = await this.$nuxt.$mutate(SET_PROFILE_PICTURE, {
          personId: this.me.id,
          fileId,
        });
        this.setUserValues({
          name: resp.person.name,
          profilePhotoId: resp.person.profilePhotoId,
        });
      } catch (error) {
        this.$nuxt.$sentry.captureException(error, {
          tags: {
            action: "setProfilePicture",
            store: "userStore",
          },
        });
        console.error("setProfilePicture error", error);
        return Promise.reject(error);
      }
    },

    async setName(name) {
      try {
        const SET_PERSON_NAME = await import("./setPersonName.graphql");
        const resp = await this.$nuxt.$mutate(SET_PERSON_NAME, {
          personId: this.me.id,
          name,
        });
        this.setUserValues({ name: resp.person.name });
      } catch (error) {
        this.$nuxt.$sentry.captureException(error, {
          tags: {
            action: "setName",
            store: "userStore",
          },
        });
        console.error("setName error", error);
        return Promise.reject(error);
      }
    },

    async setEmail(email) {
      try {
        const SETPERSONEMAIL = await import("./setPersonEmail.graphql");
        const resp = await this.$nuxt.$mutate(SETPERSONEMAIL, {
          personId: this.me.id,
          email,
        });
        this.setUserValues({ email: resp.person.email });
      } catch (error) {
        this.$nuxt.$sentry.captureException(error, {
          tags: {
            action: "setEmail",
            store: "userStore",
          },
        });
        console.error("setEmail error", error);
        return Promise.reject(error);
      }
    },

    async setPhone({ number, countryCode }) {
      try {
        const SET_PERSON_MOBILE = await import("./setPersonMobile.graphql");
        const resp = await this.$nuxt.$mutate(SET_PERSON_MOBILE, {
          personId: this.me.id,
          number,
          countryCodeId: countryCode.id,
        });
        useAccountSettingsStore().setValues("personalDetails", {
          phoneNumber: resp.phoneNumber,
        });
      } catch (error) {
        this.$nuxt.$sentry.captureException(error, {
          tags: {
            action: "setPhone",
            store: "userStore",
          },
        });
        console.error("setPhone error", error);
        return Promise.reject(error);
      }
    },

    async setPersonGoToHouse({ goToHouse, goToHouseKm }) {
      try {
        this.loading = true;
        await this.$nuxt.$mutate(SET_PERSON_GO_TO_HOUSE, {
          personId: this.me.id,
          goToHouse,
          goToHouseKm,
        });
      } catch (error) {
        this.$nuxt.$logger.error(error, "setPersonGoToHouse");
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },

    async changePassword({ currentPassword, newPassword }) {
      try {
        this.loading = true;
        await this.$nuxt.$mutate(CHANGE_PASSWORD, {
          personId: this.me.id,
          currentPassword,
          newPassword,
        });
      } catch (error) {
        this.$nuxt.$logger.error(error, "changePassword");
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },

    async setHideMobile(value) {
      const variables = {
        personId: this.me.id,
        hideMobile: value,
      };
      try {
        this.loading = true;
        await this.$nuxt.$mutate(SET_PERSON_HIDE_MOBILE, variables);
        useAccountSettingsStore().setValues("profile", { hideMobile: value });
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
      return null;
    },

    async setType(newType) {
      const variables = {
        personId: this.me.id,
        type: newType,
      };

      try {
        this.loading = true;
        const result = await this.$nuxt.$mutate(SET_PERSON_TYPE, variables, {
          showErrorToast: true,
        });
        this.setUserValues(result.person);
      } catch (error) {
        console.error("setType error", error);
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchNotifications(path) {
      try {
        const result = await this.$nuxt.$query(
          MY_NOTIFICATIONS_QUERY,
          { path },
          {
            showErrorPage: false,
            showErrorToast: false,
          }
        );
        this.notifications = result?.myNotifications || [];
      } catch (error) {
        this.$nuxt.$logger.error("Failed to fetch notifications", error);
        this.notifications = [];
      }
    },
  },
});
